import { getToken, deleteToken, Messaging } from "firebase/messaging";
import { getMessagingInstance } from "./firebaseClient";
import { vapidKey } from "../../config/firebaseConfig";
import CommunicationApiService from "../apiServices/CommunicationApiService";
import UserApiService from "../apiServices/UserApiService";

// FCM's background service worker. Lives in public/ so it is served at the
// site root; registered under a dedicated scope so it never collides with the
// Workbox PWA worker that owns "/".
const FCM_SW_URL = "/firebase-messaging-sw.js";
const FCM_SW_SCOPE = "/firebase-cloud-messaging-push-scope";

// Remembers the last token we successfully sent to the backend, so we only
// POST on first registration or when the token actually changes.
const LOCAL_TOKEN_KEY = "nine_fcm_token";

// Resolves once the registration has an ACTIVE worker. getToken() ->
// PushManager.subscribe() requires an active worker; a fresh registration may
// still be installing/waiting, which is what caused the AbortError.
function waitForActivation(
  registration: ServiceWorkerRegistration
): Promise<void> {
  if (registration.active) return Promise.resolve();
  return new Promise((resolve) => {
    const worker = registration.installing || registration.waiting;
    if (!worker) {
      resolve();
      return;
    }
    worker.addEventListener("statechange", () => {
      if (worker.state === "activated") resolve();
    });
  });
}

async function getFcmSwRegistration(): Promise<
  ServiceWorkerRegistration | undefined
> {
  if (!("serviceWorker" in navigator)) return undefined;
  try {
    const registration = await navigator.serviceWorker.register(FCM_SW_URL, {
      scope: FCM_SW_SCOPE,
    });
    await waitForActivation(registration);
    return registration;
  } catch (error) {
    console.warn("FCM service worker registration failed:", error);
    return undefined;
  }
}

const delay = (ms: number) =>
  new Promise((resolve) => setTimeout(resolve, ms));

// getToken can fail transiently — the service worker may still be activating, or
// there's a brief network blip. Retry a few times with backoff before giving up,
// re-resolving the (now-active) SW registration each attempt.
async function getTokenWithRetry(
  messaging: Messaging,
  attempts = 3
): Promise<string | null> {
  for (let attempt = 0; attempt < attempts; attempt++) {
    try {
      const serviceWorkerRegistration = await getFcmSwRegistration();
      const token = await getToken(messaging, {
        vapidKey,
        serviceWorkerRegistration,
      });
      if (token) return token;
    } catch (error) {
      console.warn(
        `FCM getToken attempt ${attempt + 1}/${attempts} failed:`,
        error
      );
    }
    if (attempt < attempts - 1) await delay(1000 * 2 ** attempt); // 1s, 2s
  }
  return null;
}

// Dedupes concurrent calls (the permission hook can run in several UIs at once)
// so we never fire duplicate getToken/register requests for the same event.
let inFlight: Promise<string | null> | null = null;

/**
 * Obtains the current FCM registration token and persists it to the backend if
 * it is new or has changed. Assumes Notification.permission is already
 * "granted". Returns the token, or null if it could not be obtained. Safe to
 * call repeatedly — it dedupes concurrent calls and no-ops once the current
 * token is already registered.
 */
export async function syncFcmToken(): Promise<string | null> {
  if (inFlight) return inFlight;

  inFlight = (async () => {
    const messaging = await getMessagingInstance();
    if (!messaging || !vapidKey) return null;

    const token = await getTokenWithRetry(messaging);
    if (!token) return null;

    if (token !== localStorage.getItem(LOCAL_TOKEN_KEY)) {
      try {
        await CommunicationApiService.registerUserFcmToken(token);
        localStorage.setItem(LOCAL_TOKEN_KEY, token);
      } catch (error) {
        // Leave the token uncached so the next sync retries the POST.
        console.warn("registerUserFcmToken failed:", error);
      }
    }
    return token;
  })();

  try {
    return await inFlight;
  } finally {
    inFlight = null;
  }
}

/** True once a token has been obtained AND successfully sent to the backend. */
export function isFcmTokenRegistered(): boolean {
  return !!localStorage.getItem(LOCAL_TOKEN_KEY);
}

/**
 * Registers a token obtained externally — e.g. the native Android layer hands
 * us its FCM token via the bridge (the WebView can't mint its own). Deduped
 * against the local marker so we only POST when it's new/changed.
 */
export async function registerProvidedToken(
  token: string,
  force = false
): Promise<void> {
  if (!token) return;
  // Skip only when we're sure it's already registered (local cache match) AND
  // the caller hasn't forced it (e.g. the server list says it's missing).
  if (!force && token === localStorage.getItem(LOCAL_TOKEN_KEY)) return;
  try {
    await CommunicationApiService.registerUserFcmToken(token);
    localStorage.setItem(LOCAL_TOKEN_KEY, token);
  } catch (error) {
    console.warn("registerUserFcmToken (native) failed:", error);
  }
}

/**
 * Reconciles this device's registration against the user's server-side token
 * list (profile.fcmTokens). The backend stores multiple tokens per user and
 * prunes dead ones, so if our locally-cached token is no longer in that list it
 * was pruned/rotated — clear the marker so syncFcmToken re-registers it. Then
 * ensure a token is registered.
 */
export async function reconcileFcmRegistration(
  serverTokens: string[]
): Promise<void> {
  const localToken = localStorage.getItem(LOCAL_TOKEN_KEY);
  if (localToken && !serverTokens.includes(localToken)) {
    localStorage.removeItem(LOCAL_TOKEN_KEY);
  }
  await syncFcmToken();
}

/**
 * Deregisters this device on the backend, deletes the local FCM token, and
 * clears the cached copy. Intended for logout / account deletion so a shared
 * device stops receiving the previous user's pushes. The backend deregister is
 * authoritative (prunes the token from profile.fcmTokens); the local deletes are
 * belt-and-suspenders.
 *
 * NOTE: the backend call must be issued while the auth cookie is still present.
 * logout() invokes this before clearing cookies, and postRequest dispatches the
 * request synchronously (with the current Bearer token) before its first await,
 * so the token is captured even though logout deletes the cookie immediately
 * after. All steps are best-effort — failures never block logout.
 */
export async function clearFcmToken(): Promise<void> {
  const registeredToken = localStorage.getItem(LOCAL_TOKEN_KEY);
  if (registeredToken) {
    try {
      await UserApiService.deregisterUserFcmToken(registeredToken);
    } catch (error) {
      console.warn("deregisterUserFcmToken failed:", error);
    }
  }
  try {
    const messaging = await getMessagingInstance();
    if (messaging) await deleteToken(messaging);
  } catch {
    /* best effort */
  }
  localStorage.removeItem(LOCAL_TOKEN_KEY);
}
